import { computed, ref } from 'vue'

import api from '@/store/api'
import { resolvePlans } from '@/view/marketing/marketingSeries'

const readOptions = { receive: false, save: false, redirectOnForbidden: false }
const writeOptions = { receive: false, redirectOnForbidden: false }

export function useMarketing() {
  const agent = ref(null)
  const plans = ref([])
  const performance = ref(null)
  const signals = ref(null)
  const building = ref(false)
  const loading = ref(false)
  const error = ref('')

  const currentPlan = computed(() => plans.value[0] || null)

  const errorOf = e => e?.response?.data?.errors?.[0] || e?.data?.errors?.[0] || e?.message || 'Something went wrong.'

  const loadAgent = async () => {
    try {
      const res = await api.call('GET', 'marketing_agent', {}, readOptions)
      agent.value = res?.data?.data || null
    } catch (e) {
      if (e?.response?.status === 404 || e?.status === 404) agent.value = null
      else error.value = errorOf(e)
    }
  }

  const loadPlans = async () => {
    const res = await api.call('GET', 'marketing_plans', {}, readOptions)
    plans.value = resolvePlans(res?.data || res)
  }

  const loadSignals = async () => {
    const res = await api.call('GET', 'marketing_signals', {}, readOptions)
    signals.value = res?.data || res
  }

  const load = async () => {
    loading.value = true
    error.value = ''
    try {
      await loadAgent()
      if (agent.value) await loadPlans()
    } catch (e) {
      error.value = errorOf(e)
    } finally {
      loading.value = false
    }
  }

  const createAgent = async settings => {
    const res = await api.call('POST', 'marketing_agent', { marketing_agent: settings }, writeOptions)
    agent.value = res?.data?.data || null
    await loadPlans()
  }

  // Whoever saves the settings (the shell drawer or a header button), the page that
  // owns the first-plan poll hears about it and starts or stops polling.
  const agentSavedListeners = new Set()
  const onAgentSaved = listener => {
    agentSavedListeners.add(listener)
    return () => agentSavedListeners.delete(listener)
  }

  const updateAgent = async settings => {
    const res = await api.call('PUT', 'marketing_agent', { marketing_agent: settings }, writeOptions)
    agent.value = res?.data?.data || agent.value
    agentSavedListeners.forEach(listener => listener(settings))
  }

  const replaceItem = updated => {
    plans.value = plans.value.map(plan => ({
      ...plan,
      items: plan.items.map(item => (item.id === updated.id ? { ...item, attributes: updated.attributes } : item))
    }))
  }

  const itemCall = async (method, path, body) => {
    const res = await api.call(method, path, body, writeOptions)
    replaceItem(res?.data?.data)
    return res?.data?.data
  }

  const editItem = (id, copy) => itemCall('PUT', `marketing_plan_items/${id}`, { marketing_plan_item: { copy } })
  const approveItem = id => itemCall('POST', `marketing_plan_items/${id}/approve`, {})
  const skipItem = (id, reason) => itemCall('POST', `marketing_plan_items/${id}/skip`, { reason })

  const cancelSend = id => itemCall('POST', `marketing_plan_items/${id}/cancel_send`, {})
  const redraft = id => itemCall('POST', `marketing_plan_items/${id}/redraft`, {})
  const markDone = id => itemCall('POST', `marketing_plan_items/${id}/done`, {})

  // After "Turn it on" the first plan arrives from a background job. Poll until it does,
  // give up after maxMs, and let the page stop the loop when it unmounts.
  let waitToken = 0
  const stopWaiting = () => {
    waitToken++
    building.value = false
  }
  const sleep = ms => new Promise(resolve => setTimeout(resolve, ms))
  const waitForPlan = async ({ intervalMs = 5000, maxMs = 120000 } = {}) => {
    const token = ++waitToken
    building.value = true
    const startedAt = Date.now()
    while (token === waitToken && Date.now() - startedAt < maxMs) {
      await sleep(intervalMs)
      if (token !== waitToken) break
      try {
        await loadPlans()
      } catch (e) {
        error.value = errorOf(e)
      }
      if (token !== waitToken) break
      if (plans.value.length) {
        building.value = false
        return true
      }
    }
    if (token === waitToken) building.value = false
    return false
  }

  let performanceRequest = 0
  const loadPerformance = async range => {
    const request = ++performanceRequest
    error.value = ''
    performance.value = null
    try {
      const res = await api.call(
        'GET',
        `marketing_performance?range=${encodeURIComponent(range || '90d')}`,
        {},
        readOptions
      )
      if (request === performanceRequest) performance.value = res?.data || res
    } catch (e) {
      if (request === performanceRequest) error.value = errorOf(e)
    }
  }

  return {
    agent,
    plans,
    currentPlan,
    performance,
    signals,
    building,
    loading,
    error,
    load,
    loadAgent,
    createAgent,
    updateAgent,
    onAgentSaved,
    editItem,
    approveItem,
    skipItem,
    cancelSend,
    redraft,
    markDone,
    loadSignals,
    waitForPlan,
    stopWaiting,
    loadPerformance,
    errorOf
  }
}
